import {
  theme as appTheme,
  Z_INDEXES,
  TRANSITIONS,
  asymmetricTransition,
  getAsymmetricTransitionValue,
} from '@traba/theme'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Button, ButtonVariant } from '../Button/Button'
import { Text } from '../Text'
import type {
  GuidedTourLabels,
  TourStep,
  TourStepPlacement,
} from '@traba/types'

const TOOLTIP_GAP = 12
const VIEWPORT_MARGIN = 16

const TooltipCard = styled.div<{
  $visible: boolean
  $maxWidth: number
  $animate: boolean
}>`
  position: fixed;
  z-index: ${Z_INDEXES.MODAL + 2};
  background: ${({ theme }) => theme.colors.White};
  border: 1px solid ${({ theme }) => theme.colors.Grey20};
  border-radius: ${({ theme }) => theme.border.radius};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: ${({ theme }) => theme.space.sm}px ${({ theme }) => theme.space.med}px;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  width: max-content;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  ${({ $visible, $animate }) =>
    $animate
      ? `transition: top ${TRANSITIONS.normal} ${TRANSITIONS.ease.out}, left ${TRANSITIONS.normal} ${TRANSITIONS.ease.out}, ${getAsymmetricTransitionValue($visible, 'opacity', TRANSITIONS.normal)};`
      : asymmetricTransition($visible, 'opacity', TRANSITIONS.normal)}
`

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.space.xs}px;
  gap: ${({ theme }) => theme.space.xxs}px;
`

const NavButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xxs}px;
  margin-left: auto;
`

function computePlacement(
  targetRect: DOMRect,
  tooltipRect: { width: number; height: number },
  preferred: TourStepPlacement,
  spotlightPadding: number,
): { top: number; left: number } {
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Offset from the spotlight edge (targetRect + spotlightPadding), not the raw element
  const totalGap = spotlightPadding + TOOLTIP_GAP

  const fitsBottom =
    targetRect.bottom + totalGap + tooltipRect.height + VIEWPORT_MARGIN < vh
  const fitsTop =
    targetRect.top - totalGap - tooltipRect.height - VIEWPORT_MARGIN > 0
  const fitsRight =
    targetRect.right + totalGap + tooltipRect.width + VIEWPORT_MARGIN < vw
  const fitsLeft =
    targetRect.left - totalGap - tooltipRect.width - VIEWPORT_MARGIN > 0

  let top: number
  let left: number

  const resolvedPlacement =
    preferred === 'auto'
      ? fitsBottom
        ? 'bottom'
        : fitsTop
          ? 'top'
          : fitsRight
            ? 'right'
            : fitsLeft
              ? 'left'
              : 'bottom'
      : preferred

  switch (resolvedPlacement) {
    case 'bottom':
      top = targetRect.bottom + totalGap
      left = targetRect.left
      break
    case 'top':
      top = targetRect.top - totalGap - tooltipRect.height
      left = targetRect.left
      break
    case 'right':
      top = targetRect.top
      left = targetRect.right + totalGap
      break
    case 'left':
      top = targetRect.top
      left = targetRect.left - totalGap - tooltipRect.width
      break
  }

  // Clamp to viewport
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, vw - tooltipRect.width - VIEWPORT_MARGIN),
  )
  top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(top, vh - tooltipRect.height - VIEWPORT_MARGIN),
  )

  return { top, left }
}

const DEFAULT_LABELS: Required<GuidedTourLabels> = {
  next: 'Next',
  back: 'Back',
  skip: 'Skip tour',
  done: 'Done',
  stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
}

interface GuidedTourTooltipProps {
  step: TourStep
  stepIndex: number
  totalSteps: number
  targetRect: DOMRect
  maxWidth: number
  spotlightPadding: number
  labels?: GuidedTourLabels
  onNext: () => void
  onBack: () => void
  onSkip: () => void
}

export function GuidedTourTooltip({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  maxWidth,
  spotlightPadding,
  labels: userLabels,
  onNext,
  onBack,
  onSkip,
}: GuidedTourTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })
  const [visible, setVisible] = useState(false)
  const [canAnimate, setCanAnimate] = useState(false)
  const hasInitialized = useRef(false)

  const labels = { ...DEFAULT_LABELS, ...userLabels }
  const isFirstStep = stepIndex === 0
  const isLastStep = stepIndex === totalSteps - 1
  const showBack = !isFirstStep && !step.hideBackButton

  const updatePosition = useCallback(() => {
    const el = tooltipRef.current
    if (!el) {
      return
    }
    const rect = el.getBoundingClientRect()
    const pos = computePlacement(
      targetRect,
      { width: rect.width, height: rect.height },
      step.placement ?? 'auto',
      spotlightPadding,
    )
    setPosition(pos)
  }, [targetRect, step.placement, spotlightPadding])

  // Initial fade-in on first mount only
  useEffect(() => {
    if (hasInitialized.current) {
      return
    }
    const raf = requestAnimationFrame(() => {
      updatePosition()
      // Small delay so the browser paints at opacity 0 first, then transitions to 1
      requestAnimationFrame(() => {
        setVisible(true)
        hasInitialized.current = true
        // Enable position transitions after the first fade-in completes
        setTimeout(() => setCanAnimate(true), 200)
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [updatePosition])

  // Smoothly reposition when targetRect or step changes (no fade, CSS handles the slide)
  useEffect(() => {
    if (!hasInitialized.current) {
      return
    }
    updatePosition()
  }, [targetRect, updatePosition, stepIndex])

  return (
    <TooltipCard
      ref={tooltipRef}
      $visible={visible}
      $maxWidth={maxWidth}
      $animate={canAnimate}
      style={{ top: position.top, left: position.left }}
    >
      <Text
        variant="body2"
        style={{ color: appTheme.colors.Grey60, marginBottom: 4, fontSize: 12 }}
      >
        {labels.stepOf(stepIndex + 1, totalSteps)}
      </Text>
      <Text variant="h5" style={{ marginBottom: 8 }}>
        {step.title}
      </Text>
      <Text variant="body2">{step.content}</Text>
      <NavRow>
        <Button variant={ButtonVariant.LINK} onClick={onSkip} slim>
          {labels.skip}
        </Button>
        <NavButtons>
          {showBack && (
            <Button variant={ButtonVariant.OUTLINED} onClick={onBack} slim>
              {labels.back}
            </Button>
          )}
          <Button variant={ButtonVariant.FILLED} onClick={onNext} slim>
            {isLastStep ? labels.done : labels.next}
          </Button>
        </NavButtons>
      </NavRow>
    </TooltipCard>
  )
}
